import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import type { PointsOutletContext } from './PointsGamesLayout'
import {
  fetchBerThongSheet,
  purchaseBerThongSlots,
  uploadBerThongReceipt,
  type BerThongSheet,
  type BerThongSlot,
} from '../../services/berThongService'
import { USER_SUMMARY_UPDATED_EVENT } from '../../services/pointsService'
import {
  BerThongPoster,
  BER_THONG_DEFAULT_VISUAL_THEME_ID,
  generateBerThongReceiptDataUrl,
} from '@huay/shared/ber-thong'
import {
  formatCloseTime,
  formatCountdown,
  huayKeyOf,
  isBettingClosed,
  isClosingSoon,
  sheetBetLabel,
} from './berThongUi'
import './berThongGame.css'

const BerThongGamePage: React.FC = () => {
  const navigate = useNavigate()
  const { sheetId = '' } = useParams<{ sheetId: string }>()
  const { cashBalance, refreshUserSummary } = useOutletContext<PointsOutletContext>()
  const [slots, setSlots] = useState<BerThongSlot[]>([])
  const [sheet, setSheet] = useState<BerThongSheet | null>(null)
  const [selectedNumbers, setSelectedNumbers] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [buying, setBuying] = useState(false)
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [])

  const loadSheet = useCallback(async (id: string) => {
    setLoading(true)
    try {
      const res = await fetchBerThongSheet(id)
      setSheet(res.data?.sheet || null)
      setSlots(res.data?.slots || [])
      setSelectedNumbers([])
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'โหลดแผ่นไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'ลองใหม่อีกครั้ง',
      })
      navigate('/points/games/ber-thong')
    } finally {
      setLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    if (sheetId) void loadSheet(sheetId)
  }, [sheetId, loadSheet])

  const total = useMemo(() => {
    if (!sheet) return 0
    return selectedNumbers.length * Number(sheet.pricePerSlot || 0)
  }, [selectedNumbers, sheet])

  const bettingClosed = isBettingClosed(sheet, nowMs)
  const activeCloseLabel = formatCloseTime(sheet?.closeAt)
  const activeCountdown = formatCountdown(sheet?.closeAt, nowMs)
  const activeSoon = isClosingSoon(sheet?.closeAt, nowMs)
  const backHuayPath = sheet
    ? `/points/games/ber-thong/huay/${encodeURIComponent(huayKeyOf(sheet))}`
    : '/points/games/ber-thong'

  useEffect(() => {
    if (bettingClosed && selectedNumbers.length > 0) setSelectedNumbers([])
  }, [bettingClosed, selectedNumbers.length])

  const toggleNumber = (number: string, available: boolean) => {
    if (bettingClosed || !available) return
    setSelectedNumbers((prev) =>
      prev.includes(number) ? prev.filter((n) => n !== number) : [...prev, number].sort(),
    )
  }

  const handleBuy = async () => {
    if (!sheet || selectedNumbers.length === 0 || bettingClosed) return
    if (cashBalance < total) {
      await Swal.fire({ icon: 'warning', title: 'ยอดเงินไม่พอ', text: `ต้องการ ${total} ฿` })
      return
    }
    const betLabel = sheetBetLabel(sheet)
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันซื้อเบอร์',
      html: `แผ่น <strong>${betLabel}</strong><br/>เบอร์ <strong>${selectedNumbers.join(', ')}</strong><br/>ยอดรวม <strong>${total} ฿</strong>`,
      showCancelButton: true,
      confirmButtonText: 'ซื้อ',
      cancelButtonText: 'ยกเลิก',
    })
    if (!confirm.isConfirmed) return

    setBuying(true)
    try {
      const res = await purchaseBerThongSlots(sheet.id, selectedNumbers)
      const purchase = res.data?.purchase
      const receiptDataUrl = purchase
        ? generateBerThongReceiptDataUrl({
            sheetCode: purchase.sheetCode || sheet.sheetCode,
            sheetTitle: purchase.sheetTitle || sheet.title,
            huayName: purchase.huayName || sheet.huayName,
            drawDate: purchase.drawDate || sheet.drawDate,
            betTypeLabel: betLabel,
            numbers: purchase.numbers || selectedNumbers,
            unitPrice: purchase.unitPrice ?? sheet.pricePerSlot,
            totalAmount: purchase.totalAmount ?? total,
            purchaseId: purchase.id,
            purchasedAt: purchase.createdAt || new Date().toISOString(),
          })
        : ''

      let receiptSaved = false
      if (purchase?.id && receiptDataUrl) {
        try {
          await uploadBerThongReceipt(purchase.id, receiptDataUrl, 'image/png')
          receiptSaved = true
        } catch {
          receiptSaved = false
        }
      }

      await Swal.fire({
        icon: 'success',
        title: 'ซื้อสำเร็จ',
        text: `${betLabel} · เบอร์ ${(purchase?.numbers || selectedNumbers).join(', ')} · ${purchase?.totalAmount ?? total} ฿${
          receiptDataUrl
            ? (receiptSaved ? ' · เก็บใบเสร็จในระบบแล้ว' : ' · ออกใบเสร็จแล้ว (อัปโหลดยังไม่สำเร็จ)')
            : ''
        }`,
        imageUrl: receiptDataUrl || undefined,
        imageAlt: 'ใบเสร็จเบอร์ทอง',
        width: receiptDataUrl ? 560 : undefined,
        confirmButtonText: 'ปิด',
      })
      window.dispatchEvent(new Event(USER_SUMMARY_UPDATED_EVENT))
      await refreshUserSummary?.()
      await loadSheet(sheet.id)
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'ซื้อไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'ลองใหม่อีกครั้ง',
      })
      if (sheetId) await loadSheet(sheetId)
    } finally {
      setBuying(false)
    }
  }

  if (loading) {
    return <div className="htd-bt__loading">กำลังโหลดแผ่น...</div>
  }

  if (!sheet) {
    return (
      <div className="htd-bt">
        <div className="htd-bt__empty">
          <h2>ไม่พบแผ่น</h2>
          <Link to="/points/games/ber-thong" className="htd-bt__link">กลับ Lobby</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="htd-bt">
      <div className="htd-bt__hero">
        <div className="htd-bt__hero-copy">
          <Link to={backHuayPath} className="htd-bt__link">← รายการแผ่น</Link>
          <h2 className="htd-bt__title">{sheet.title}</h2>
          <p className="htd-bt__sub">
            {sheet.sheetCode ? `${sheet.sheetCode} · ` : ''}
            {sheet.huayName} · {sheetBetLabel(sheet)} · {sheet.pricePerSlot}฿/เบอร์
          </p>
          {activeCloseLabel ? (
            <p className={`htd-bt__close-line${activeSoon || bettingClosed ? ' is-urgent' : ''}`}>
              ปิดรับ {activeCloseLabel}
              {activeCountdown ? ` · ${activeCountdown}` : ''}
            </p>
          ) : null}
          {sheet.cancelIfBelowEnabled && sheet.minSoldToKeep ? (
            <p className="htd-bt__rule-hint">
              ยกเลิกทั้งแผ่นถ้าปิดรับแล้วยอดขายไม่ถึง {sheet.minSoldToKeep} เบอร์ (คืนเงินเข้ากระเป๋า)
            </p>
          ) : null}
        </div>
        <Link to="/history/games/ber-thong" className="htd-bt__link">ประวัติ</Link>
      </div>

      <div className="htd-bt__panel">
        <BerThongPoster
          themeId={sheet.visualThemeId || BER_THONG_DEFAULT_VISUAL_THEME_ID}
          title={sheet.title || 'เบอร์เงินสด'}
          pricePerSlot={sheet.pricePerSlot}
          prizeLabel={sheet.prizeLabel}
          drawDate={sheet.drawDate}
          closeLabel={
            activeCloseLabel
              ? `${activeCloseLabel}${activeCountdown ? ` · ${activeCountdown}` : ''}`
              : ''
          }
          huayName={sheet.huayName}
          sheetCode={sheet.sheetCode}
          slots={slots}
          selectedNumbers={selectedNumbers}
          interactive={!bettingClosed}
          bettingClosed={bettingClosed}
          onToggleNumber={toggleNumber}
        />

        <div className="htd-bt__cart">
          <div>
            <p className="htd-bt__cart-label">
              {bettingClosed
                ? 'ปิดรับแทงแล้ว — ไม่สามารถซื้อเบอร์ได้'
                : `${sheetBetLabel(sheet)} · เลือก ${selectedNumbers.length} เบอร์${
                    selectedNumbers.length > 0 ? `: ${selectedNumbers.join(', ')}` : ''
                  }`}
            </p>
            <p className="htd-bt__cart-total">
              รวม {total.toLocaleString()} ฿ · เงินสด {cashBalance.toLocaleString()} ฿
            </p>
          </div>
          <button
            type="button"
            className="htd-bt__buy"
            disabled={buying || bettingClosed || selectedNumbers.length === 0}
            onClick={() => void handleBuy()}
          >
            {bettingClosed ? 'ปิดรับแล้ว' : buying ? 'กำลังซื้อ...' : 'ซื้อเบอร์'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default BerThongGamePage
